/**
 * @component ry-feature
 * @category  display
 * @summary   Feature card — an icon plus a heading and description (ry-ui-only; no direct Fomantic equivalent). Tiles inside `<ry-feature-grid>` form the classic "three columns of features" landing-page pattern. The icon is opt-in via the `icon` attribute; everything else is whatever children the consumer authors.
 *
 * @attribute icon   string?   Icon name from the registry (e.g. `"settings"`, `"check"`). When set, ry-feature prepends a `[data-ry-target="icon"]` slot containing the resolved SVG/glyph. Unknown names render no slot (silent, like `<ry-icon>`).
 *
 * @content   Free-form. Typically a `<h3>` heading + a `<p>` paragraph. Children are kept as-is — only the optional icon is prepended.
 *
 * @dom
 *   ry-feature[icon?]
 *     > div.ry-feature__icon   [data-ry-target="icon"]   (only when [icon] resolves to a registered glyph)
 *       > {icon SVG / <i>}
 *     > {authored children}
 *
 * @gotcha  No `[icon]` (or an unknown icon name) renders ZERO icon slot — the children appear without a leading icon. Choose intentionally; if the icon is core to the feature, set it. If the feature is text-only, omit the attribute.
 *
 * @gotcha  Setup-once: changing the `icon` attribute after mount does NOT re-render or replace the icon slot. Set it before mount, or replace the element.
 *
 * @gotcha  The icon slot is inserted via `insertBefore(iconDiv, this.firstChild)`. If the consumer's first child is whitespace text or a comment, the icon appears BEFORE it but the visual ordering may surprise — render children adjacent to the host's open tag.
 *
 * @example basic
 * ```html
 * <ry-feature icon="settings">
 *   <h3>Easy Setup</h3>
 *   <p>One line of code to get started.</p>
 * </ry-feature>
 * ```
 *
 * @example no-icon
 * ```html
 * <ry-feature>
 *   <h3>Text-only feature</h3>
 *   <p>No icon needed.</p>
 * </ry-feature>
 * ```
 *
 * @example in-grid
 * ```html
 * <ry-feature-grid cols="3">
 *   <ry-feature icon="settings"><h3>Setup</h3><p>One line.</p></ry-feature>
 *   <ry-feature icon="zap"><h3>Fast</h3><p>Sub-second loads.</p></ry-feature>
 *   <ry-feature icon="shield"><h3>Secure</h3><p>End-to-end encrypted.</p></ry-feature>
 * </ry-feature-grid>
 * ```
 *
 * @tutorial icon-attribute-renders-slot
 * ```js
 * const el = mount('<ry-feature icon="settings"><h3>X</h3></ry-feature>');
 * const slot = el.querySelector('[data-ry-target="icon"]');
 * await expectEqual(!!slot, true, 'icon slot present');
 * // Should contain either an SVG (Lucide) or an <i> (Semantic)
 * await expectEqual(!!(slot.querySelector('svg') || slot.querySelector('i')), true);
 * ```
 *
 * @tutorial no-icon-attribute-no-slot
 * ```js
 * const el = mount('<ry-feature><h3>X</h3></ry-feature>');
 * await expectEqual(el.querySelector('[data-ry-target="icon"]'), null, 'no icon slot without [icon]');
 * ```
 *
 * @tutorial unknown-icon-name-no-slot
 * ```js
 * // Wrong assumption: bad icon name produces a placeholder. It silently
 * // skips the slot — verify the name before authoring.
 * const el = mount('<ry-feature icon="zzz-not-a-real-icon"><h3>X</h3></ry-feature>');
 * await expectEqual(el.querySelector('[data-ry-target="icon"]'), null, 'unknown name → no slot');
 * ```
 *
 * @tutorial icon-prepended-before-children
 * ```js
 * // The icon slot must come BEFORE the consumer's children in DOM order
 * // so it renders to the LEFT/TOP per the theme's layout.
 * const el = mount('<ry-feature icon="check"><h3 id="h">X</h3><p>Y</p></ry-feature>');
 * const slot = el.querySelector('[data-ry-target="icon"]');
 * const h = el.querySelector('#h');
 * const order = slot.compareDocumentPosition(h);
 * await expectEqual(
 *   (order & Node.DOCUMENT_POSITION_FOLLOWING) !== 0,
 *   true,
 *   'icon precedes the heading in DOM order',
 * );
 * ```
 *
 * @tutorial children-are-preserved
 * ```js
 * // Wrong assumption: the wrapper replaces innerHTML. It does NOT — children
 * // are kept, only an icon slot may be prepended.
 * const el = mount('<ry-feature icon="check"><h3>Title</h3><p>Body</p></ry-feature>');
 * await expectEqual(el.querySelectorAll('h3, p').length, 2, 'both authored children survive');
 * ```
 *
 * @see ry-feature-grid
 * @see ry-stat
 * @see ry-icon
 */

/*
 * <ry-feature-grid> — companion layout container for <ry-feature>.
 *
 * Pure CSS styling hook — no JS. The host accepts a `cols` attribute (e.g.
 * "2", "3", "4") that the theme reads to set grid-template-columns. Children
 * become grid cells and the theme handles responsive collapse on narrow
 * viewports.
 *
 * The docs-compile pipeline only handles one @component per file, so this
 * sibling export is documented in prose and verified by vitest only. See
 * tests/components/ry-feature.test.ts for the behavior contract.
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

export class RyFeature extends RyElement {
  setup(): void {
    this.#transformMarkup();
  }

  #transformMarkup(): void {
    if (this.$('[data-ry-target="icon"]')) return;

    const iconName = this.getAttribute('icon');
    if (!iconName) return;

    const iconSvg = getIcon(iconName);
    if (!iconSvg) return;

    const iconDiv = document.createElement('div');
    iconDiv.setAttribute('data-ry-target', 'icon');
    iconDiv.className = 'ry-feature__icon';
    iconDiv.innerHTML = iconSvg;
    this.insertBefore(iconDiv, this.firstChild);
  }
}

export class RyFeatureGrid extends RyElement {}

customElements.define('ry-feature', RyFeature);
customElements.define('ry-feature-grid', RyFeatureGrid);
